import React, { useEffect, useRef, useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";

const LANGS = [
  { code: "en", label: "English" },
  { code: "am", label: "አማርኛ (Amharic)" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "ru", label: "Русский" },
  { code: "it", label: "Italiano" },
  { code: "sv", label: "Svenska" },
  { code: "fi", label: "Suomi" },
  { code: "hi", label: "हिन्दी (Hindi)" },
  { code: "ja", label: "日本語" },
  { code: "zh-CN", label: "中文 (Chinese)" },
];
const INCLUDED = "am,fr,es,ru,it,sv,fi,hi,ja,zh-CN";

function readLang() {
  const m = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "en";
}

// Poll for Google's hidden select and apply the target language.
function applyLang(code, tries = 0) {
  const combo = document.querySelector(".goog-te-combo");
  if (combo) {
    combo.value = code;
    combo.dispatchEvent(new Event("change", { bubbles: true }));
    return;
  }
  if (tries < 40) setTimeout(() => applyLang(code, tries + 1), 200);
}

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const ref = useRef();

  useEffect(() => {
    const current = readLang();
    setLang(current);
    if (!window.__gtInjected) {
      window.__gtInjected = true;
      window.googleTranslateElementInit = () => {
        try {
          new window.google.translate.TranslateElement(
            { pageLanguage: "en", includedLanguages: INCLUDED, autoDisplay: false },
            "google_translate_element"
          );
        } catch (e) { /* noop */ }
      };
      const s = document.createElement("script");
      s.src = "https://translate.googleapis.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      document.body.appendChild(s);
    }
    // Re-apply the chosen language after (re)load / SPA navigation.
    if (current !== "en") applyLang(current);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const setCookie = (val) => {
    const host = window.location.hostname;
    const clear = "googtrans=;expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    document.cookie = clear + "path=/;";
    document.cookie = clear + `path=/;domain=.${host};`;
    document.cookie = clear + `path=/;domain=${host};`;
    if (val) {
      document.cookie = `googtrans=${val};path=/`;
      document.cookie = `googtrans=${val};path=/;domain=.${host}`;
      document.cookie = `googtrans=${val};path=/;domain=${host}`;
    }
  };

  const choose = (code) => {
    setOpen(false);
    if (code === "en") { setCookie(null); setLang("en"); window.location.reload(); return; }
    setCookie(`/en/${code}`);
    setLang(code);
    applyLang(code);
  };

  const active = LANGS.find((l) => l.code === lang) || LANGS[0];
  const shortLabel = active.code === "en" ? "EN" : active.label.split(" ")[0];

  return (
    <div className="relative notranslate" ref={ref}>
      <div id="google_translate_element" aria-hidden className="gt-hidden" />
      <button onClick={() => setOpen(!open)} data-testid="language-switcher" aria-label="Change language"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1F4D2E] hover:text-[#C1272D] transition-colors border border-[#1F4D2E]/20 rounded-full px-3 py-1.5">
        <Globe size={16} /> <span className="hidden sm:inline">{shortLabel}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 max-h-[70vh] overflow-y-auto bg-white border border-[#1F4D2E]/15 rounded-xl shadow-xl py-2 z-[70]" data-testid="language-menu">
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => choose(l.code)} data-testid={`lang-${l.code}`}
              className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-[#FBF8F0] ${l.code === lang ? "text-[#C1272D] font-semibold" : "text-[#1F4D2E]"}`}>
              {l.label} {l.code === lang && <Check size={15} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
